"use client";

import { startTransition, useActionState, useEffect, useState } from "react";

import { Text } from "@/components/text/text.component";

import type { GetProductQueryResult } from "@/sanity.types";

import { updateKitOptionsInTemporaryCart } from "../../actions";
import { ProductOptionsProductValue } from "./productOptionsProductValue.component";

/** Product Included Products */
export const ProductOptionsProduct = ({
  slug,
  title,
  optionValues,
  optionId = "product",
}: ProductOptionsProductProps) => {
  const [includedProduct, setIncludedProduct] = useState<string | null>();
  const [, updateKitOptionsAction] = useActionState(
    updateKitOptionsInTemporaryCart,
    { success: false },
  );
  if (!slug) return null;
  const fieldsetName = `shop:${slug}:${optionId}`;
  useEffect(() => {
    // check localStorage for saved included product value and set it
    const savedValue = localStorage.getItem(fieldsetName);
    // if saved value exists and is valid, set it as the included product, otherwise default to first option or null
    setIncludedProduct(savedValue || optionValues?.[0]?.id || null);
  }, []);

  const renderOptionValues = () => {
    if (!optionValues?.length) return null;
    return optionValues.map(
      ({ title, description, image, id, product }, index) => {
        // if no included product is selected, default to first option
        const selected = id === includedProduct;
        return (
          <ProductOptionsProductValue
            key={index}
            title={title}
            description={description}
            image={image}
            optionId={optionId}
            id={id}
            product={product}
            fieldsetName={fieldsetName}
            selected={selected}
            onChange={(value) => {
              setIncludedProduct(value);
              localStorage.setItem(fieldsetName, value);
              const productForm = document.getElementById("product-form");
              if (!(productForm instanceof HTMLFormElement)) return;
              startTransition(() => {
                updateKitOptionsAction(new FormData(productForm));
              });
            }}
          />
        );
      },
    );
  };
  return (
    <fieldset
      name={fieldsetName}
      data-testid="product-included-product"
      className="mb-6 flex flex-col">
      <legend className="mb-4">
        <Text variant="h5" data-testid="product-included-product-title">
          {title || "Select included product"}
        </Text>
      </legend>
      <div
        data-testid="product-included-Product-list"
        className="flex flex-col gap-4">
        {renderOptionValues()}
      </div>
    </fieldset>
  );
};

export type ProductOptionsProductProps = {
  slug: string;
  title?: string;
  optionValues: ProductQueryOptionValue[] | null;
  // The parent option id in sanity i.e "freeProductOptions"
  optionId?: string;
};

/** Option value shape from the product query (options[].optionValues[]). */
export type ProductQueryOptionValue = NonNullable<
  NonNullable<
    NonNullable<GetProductQueryResult>["options"]
  >[number]["optionValues"]
>[number];

export type ProductQueryOption = NonNullable<GetProductQueryResult>["options"];
